import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from 'src/app/auth/auth.service';
import {
  RenameContentApiService,
  RenameContentChange,
  RenameContentDbSource,
  RenameContentLatestApplyResponse,
  RenameContentRunReport,
  RenameContentScanReport,
  RenameContentScope,
  SpecialReason,
} from './rename-content-api.service';
import { downloadRenameContentPdf, PdfReportRow } from './rename-content-pdf.util';

type ScanRow = {
  source: string;
  collectionOrTable: string;
  id: string | number | null;
  fieldOrColumn: string;
  value: string;
  isSpecialCase?: boolean;
  specialReasons?: SpecialReason[];
};

@Component({
  selector: 'app-rename-content',
  templateUrl: './rename-content.component.html',
  styleUrls: ['./rename-content.component.sass'],
  providers: [ConfirmationService],
})
export class RenameContentComponent implements OnInit {
  from = 'Garden Joy';
  to = 'Flourish';

  /** Optional scope — leave empty for full Mongo+MySQL scan (legacy behavior). */
  dbSource: RenameContentDbSource | null = null;
  selectedTargets: string[] = [];
  targetOptions: Array<{ label: string; value: string }> = [];
  loadingTargets = false;

  readonly dbSourceOptions: Array<{ label: string; value: RenameContentDbSource }> = [
    { label: 'Mongo', value: 'mongo' },
    { label: 'MySQL', value: 'mysql' },
  ];

  loading = false;
  loadingLabel = 'Running… this can take a while on a full scan.';
  isAdmin = false;
  loadingLatestApply = false;

  lastDryRun: RenameContentRunReport | null = null;
  lastApply: RenameContentRunReport | null = null;
  lastScan: RenameContentScanReport | null = null;

  /** Last successful Apply still available for rollback (persisted up to 1 month). */
  pendingRollback: RenameContentLatestApplyResponse | null = null;

  resultMode: 'dry-run' | 'apply' | 'rollback' | 'scan' | null = null;
  runChanges: RenameContentChange[] = [];
  scanRows: ScanRow[] = [];

  /** Selected change keys. Default: everything selected; uncheck rows to discard. */
  selectedKeys: Record<string, boolean> = {};

  /** Dry-run results table pagination (for "select this page"). */
  resultsFirst = 0;
  resultsRows = 25;

  /** Rollback preview dialog. */
  rollbackDialogVisible = false;
  rollbackDialogRows = 25;
  rollbackDialogFirst = 0;
  rollbackApplying = false;

  /**
   * Writable checkbox models (PrimeNG ngModel cannot bind to getters).
   * Kept in sync via syncSelectionCheckboxes().
   */
  allSelectedChecked = false;
  pageSelectedChecked = false;
  pagePartiallySelected = false;

  constructor(
    private renameContentApi: RenameContentApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.hasRole('ROLE_ADMIN');
    if (this.isAdmin) {
      void this.refreshPendingRollback();
    }
  }

  get selectableChanges(): RenameContentChange[] {
    return this.runChanges.filter((c) => !!c.key);
  }

  get safeChanges(): RenameContentChange[] {
    return this.selectableChanges.filter((c) => !c.isSpecialCase);
  }

  get specialChanges(): RenameContentChange[] {
    return this.selectableChanges.filter((c) => !!c.isSpecialCase);
  }

  get selectedCount(): number {
    return this.selectableChanges.filter((c) => this.selectedKeys[c.key!]).length;
  }

  get selectedSafeCount(): number {
    return this.safeChanges.filter((c) => this.selectedKeys[c.key!]).length;
  }

  get selectedSpecialCount(): number {
    return this.specialChanges.filter((c) => this.selectedKeys[c.key!]).length;
  }

  /** Unchecked keys (safe + special) — sent on apply so the payload stays small. */
  get discardedKeys(): string[] {
    return this.selectableChanges.filter((c) => !this.selectedKeys[c.key!]).map((c) => c.key!);
  }

  get allSelected(): boolean {
    return this.allSelectedChecked;
  }

  /** Selectable rows on the current results page (paginator slice). */
  get currentPageSelectable(): RenameContentChange[] {
    return this.runChanges
      .slice(this.resultsFirst, this.resultsFirst + this.resultsRows)
      .filter((c) => !!c.key);
  }

  get canApply(): boolean {
    return (
      this.isAdmin &&
      !this.loading &&
      this.resultMode === 'dry-run' &&
      !!this.lastDryRun?.success &&
      this.lastDryRun.mode === 'dry-run' &&
      !!this.lastDryRun.planId &&
      this.lastDryRun.from === this.from.trim() &&
      this.lastDryRun.to === this.to.trim() &&
      this.selectedCount > 0
    );
  }

  get canRollback(): boolean {
    return (
      this.isAdmin &&
      !this.loading &&
      !this.loadingLatestApply &&
      !!this.pendingRollback?.available &&
      !!this.pendingRollback.applyId &&
      (this.pendingRollback.appliedCount ?? this.pendingRollback.changes?.length ?? 0) > 0
    );
  }

  get rollbackChanges(): RenameContentChange[] {
    return this.pendingRollback?.changes || [];
  }

  get hasResults(): boolean {
    return !!(this.lastDryRun || this.lastApply || this.lastScan);
  }

  get summaryText(): string {
    if (this.resultMode === 'scan' && this.lastScan) {
      const safe = this.scanRows.filter((r) => !r.isSpecialCase).length;
      const special = this.scanRows.filter((r) => r.isSpecialCase).length;
      return `Discovery scan: ${safe} safe, ${special} special-case (${this.formatDuration(this.lastScan.durationMs)})`;
    }
    const report =
      this.resultMode === 'apply' || this.resultMode === 'rollback'
        ? this.lastApply
        : this.lastDryRun;
    if (!report) return '';
    const safe = report.safeChangeCount ?? (report.changes || []).filter((c) => !c.isSpecialCase).length;
    const special = report.specialChangeCount ?? (report.changes || []).filter((c) => c.isSpecialCase).length;
    if (report.mode === 'dry-run') {
      return (
        `dry-run: ${this.selectedCount} selected ` +
        `(${this.selectedSafeCount} safe, ${this.selectedSpecialCount} special), ` +
        `${this.discardedKeys.length} discarded · no writes (${this.formatDuration(report.durationMs)})`
      );
    }
    if (report.mode === 'rollback') {
      return `rollback: restored ${report.appliedCount ?? 0} value(s) (${this.formatDuration(report.durationMs)})`;
    }
    const applied = ` · applied ${report.appliedCount ?? 0}`;
    const discarded =
      report.discardedCount != null ? ` · discarded ${report.discardedCount}` : '';
    const rollbackHint = report.applyId ? ' · rollback available (up to 1 month)' : '';
    return `${report.mode}: ${safe} safe, ${special} special-case${applied}${discarded}${rollbackHint} (${this.formatDuration(report.durationMs)})`;
  }

  get targetsLabel(): string {
    return this.dbSource === 'mysql' ? 'Tables' : 'Collections';
  }

  async onDbSourceChange(source: RenameContentDbSource | null): Promise<void> {
    this.dbSource = source;
    this.selectedTargets = [];
    this.targetOptions = [];
    if (!source) return;

    this.loadingTargets = true;
    try {
      const response = await this.renameContentApi.listTargets(source);
      if (!response.success) {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to load targets',
          detail: response.errorMessage || 'Unknown error',
        });
        return;
      }
      this.targetOptions = (response.names || []).map((name) => ({
        label: name,
        value: name,
      }));
    } catch (error: any) {
      this.handleHttpError(error, 'Failed to load targets');
    } finally {
      this.loadingTargets = false;
    }
  }

  private currentScope(): RenameContentScope | undefined {
    if (!this.dbSource) return undefined;
    return {
      dbSource: this.dbSource,
      ...(this.selectedTargets.length ? { targets: [...this.selectedTargets] } : {}),
    };
  }

  formatDuration(durationMs?: number): string {
    const totalSeconds = Math.max(0, Math.floor((durationMs ?? 0) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':');
  }

  formatReasons(reasons?: SpecialReason[]): string {
    return (reasons || []).join(', ');
  }

  isRowSelected(row: RenameContentChange): boolean {
    return !!(row.key && this.selectedKeys[row.key]);
  }

  toggleRow(row: RenameContentChange, checked: boolean): void {
    if (!row.key) return;
    this.selectedKeys = { ...this.selectedKeys, [row.key]: !!checked };
    this.syncSelectionCheckboxes();
  }

  onAllResultsChange(event: { checked?: boolean } | boolean): void {
    const checked = typeof event === 'boolean' ? event : !!event?.checked;
    this.toggleSelectAll(checked);
  }

  onPageResultsChange(event: { checked?: boolean } | boolean): void {
    const checked = typeof event === 'boolean' ? event : !!event?.checked;
    this.toggleSelectPage(checked);
  }

  /** Select / unselect only the rows on the current results page. */
  toggleSelectPage(checked: boolean): void {
    const next: Record<string, boolean> = { ...this.selectedKeys };
    for (const change of this.currentPageSelectable) {
      next[change.key!] = checked;
    }
    this.selectedKeys = next;
    this.syncSelectionCheckboxes();
  }

  /** Select / unselect every row in the full dry-run list. */
  toggleSelectAll(checked: boolean): void {
    const next: Record<string, boolean> = { ...this.selectedKeys };
    for (const change of this.selectableChanges) {
      next[change.key!] = checked;
    }
    this.selectedKeys = next;
    this.syncSelectionCheckboxes();
  }

  onResultsPage(event: { first?: number; rows?: number }): void {
    this.resultsFirst = event.first ?? 0;
    if (event.rows != null) {
      this.resultsRows = event.rows;
    }
    this.syncSelectionCheckboxes();
  }

  /** Default: select every row (safe + special). Uncheck any you want discarded. */
  private selectDefaultKeys(changes: RenameContentChange[]): void {
    const next: Record<string, boolean> = {};
    for (const change of changes) {
      if (!change.key) continue;
      next[change.key] = true;
    }
    this.selectedKeys = next;
    this.resultsFirst = 0;
    this.syncSelectionCheckboxes();
  }

  /** Keep master checkboxes in sync with selectedKeys (required for PrimeNG ngModel). */
  private syncSelectionCheckboxes(): void {
    const all = this.selectableChanges;
    this.allSelectedChecked =
      all.length > 0 && all.every((c) => !!this.selectedKeys[c.key!]);

    const page = this.currentPageSelectable;
    const pageSelectedCount = page.filter((c) => !!this.selectedKeys[c.key!]).length;
    this.pageSelectedChecked = page.length > 0 && pageSelectedCount === page.length;
    this.pagePartiallySelected = pageSelectedCount > 0 && pageSelectedCount < page.length;
  }

  async dryRun(): Promise<void> {
    if (!this.validateInputs()) return;
    this.loading = true;
    this.loadingLabel = 'Dry-run in progress… scanning for matches (no writes).';
    this.lastApply = null;
    try {
      const report = await this.renameContentApi.dryRun(
        this.from.trim(),
        this.to.trim(),
        this.currentScope(),
      );
      this.lastDryRun = report;
      this.resultMode = 'dry-run';
      this.runChanges = report.changes || [];
      this.scanRows = [];
      this.selectDefaultKeys(this.runChanges);

      if (!report.success) {
        this.messageService.add({
          severity: 'error',
          summary: 'Dry-run failed',
          detail: report.errorMessage || 'Unknown error',
        });
        return;
      }

      if (report.writesPerformed) {
        this.messageService.add({
          severity: 'error',
          summary: 'Dry-run safety check failed',
          detail: 'Server reported writes during dry-run. Aborting — contact eng.',
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Dry-run complete (no writes)',
        detail:
          `${report.changeCount ?? 0} match(es) selected by default ` +
          `(${report.safeChangeCount ?? 0} safe, ${report.specialChangeCount ?? 0} special). ` +
          `Review specials and uncheck any you want to discard, then Apply.`,
      });
    } catch (error: any) {
      this.handleHttpError(error, 'Dry-run failed');
    } finally {
      this.loading = false;
    }
  }

  confirmApply(): void {
    if (!this.canApply) return;
    const selected = this.selectedCount;
    const discarded = this.discardedKeys.length;
    const selectedSpecial = this.selectedSpecialCount;

    const specialWarning =
      selectedSpecial > 0
        ? `\n⚠️ Includes ${selectedSpecial} SPECIAL case(s) — review carefully (URIs, identifiers, ALL CAPS, keys, history).\n`
        : '\n';

    this.confirmationService.confirm({
      header: 'Apply content rename',
      icon: 'pi pi-exclamation-triangle',
      message:
        `This will replace "${this.from.trim()}" with "${this.to.trim()}" in ` +
        `${selected} selected field(s) (${this.selectedSafeCount} safe, ${selectedSpecial} special).\n` +
        `${discarded} unchecked row(s) will be skipped.` +
        specialWarning +
        `\nContinue?`,
      acceptLabel: 'Apply selected',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        void this.apply();
      },
    });
  }

  confirmRollback(): void {
    if (!this.canRollback || !this.pendingRollback?.applyId) return;
    this.rollbackDialogFirst = 0;
    this.rollbackDialogVisible = true;
  }

  closeRollbackDialog(): void {
    if (this.rollbackApplying) return;
    this.rollbackDialogVisible = false;
  }

  async executeRollbackFromDialog(): Promise<void> {
    if (!this.canRollback || !this.pendingRollback?.applyId || this.rollbackApplying) return;
    this.rollbackApplying = true;
    this.loading = true;
    this.loadingLabel = 'Rolling back… restoring previous values in Mongo/MySQL.';
    try {
      const applyId = this.pendingRollback.applyId;
      const report = await this.renameContentApi.rollback(applyId);
      this.lastApply = report;
      this.resultMode = 'rollback';
      this.runChanges = report.changes || [];
      this.scanRows = [];
      this.selectedKeys = {};
      this.rollbackDialogVisible = false;

      if (!report.success) {
        this.messageService.add({
          severity: 'error',
          summary: 'Rollback failed',
          detail: report.errorMessage || 'Unknown error',
        });
        await this.refreshPendingRollback();
        return;
      }

      this.pendingRollback = null;
      this.messageService.add({
        severity: 'success',
        summary: 'Rename rolled back',
        detail: `Restored ${report.appliedCount ?? 0} value(s) to their pre-apply state.`,
      });
    } catch (error: any) {
      this.handleHttpError(error, 'Rollback failed');
      await this.refreshPendingRollback();
    } finally {
      this.rollbackApplying = false;
      this.loading = false;
    }
  }

  private async apply(): Promise<void> {
    if (!this.canApply || !this.lastDryRun?.planId) return;
    const planId = this.lastDryRun.planId;
    const discarded = this.discardedKeys;
    this.loading = true;
    this.loadingLabel = 'Applying selected changes… writing to Mongo/MySQL.';
    try {
      const report = await this.renameContentApi.apply(
        this.from.trim(),
        this.to.trim(),
        planId,
        discarded,
        this.currentScope(),
      );
      this.lastApply = report;
      this.resultMode = 'apply';
      // Results table shows what was actually written.
      this.runChanges = (report.changes || []).filter((c) => c.applied === true);
      this.scanRows = [];
      this.selectedKeys = {};

      if (!report.success) {
        this.messageService.add({
          severity: 'error',
          summary: 'Apply failed',
          detail: report.errorMessage || 'Unknown error',
        });
        return;
      }

      this.setPendingRollbackFromApply(report);
      this.messageService.add({
        severity: 'success',
        summary: 'Rename applied',
        detail:
          `Updated ${report.appliedCount ?? 0} value(s)` +
          `${report.appliedSpecialCount ? ` (${report.appliedSpecialCount} special)` : ''}. ` +
          `Discarded ${report.discardedCount ?? 0}.` +
          (report.applyId ? ' Rollback is available for up to 1 month.' : ''),
      });
      this.lastDryRun = null;

      if ((report.appliedCount ?? 0) > 0) {
        this.promptApplyReportDownload(report);
      }
    } catch (error: any) {
      this.handleHttpError(error, 'Apply failed');
    } finally {
      this.loading = false;
    }
  }

  /** Offer PDF apply report right after a successful write. */
  private promptApplyReportDownload(report: RenameContentRunReport): void {
    this.confirmationService.confirm({
      header: 'Apply report',
      icon: 'pi pi-file-pdf',
      message:
        `Apply finished: ${report.appliedCount ?? 0} change(s) written` +
        `${report.discardedCount ? `, ${report.discardedCount} discarded` : ''}.\n\n` +
        `Download the apply PDF report now? (You can also use Download PDF / JSON anytime.)`,
      acceptLabel: 'Download PDF',
      rejectLabel: 'Later',
      accept: () => {
        try {
          this.downloadPdf();
        } catch (error: any) {
          this.messageService.add({
            severity: 'error',
            summary: 'PDF report failed',
            detail: error?.message || 'Could not open the PDF report window.',
          });
        }
      },
    });
  }

  private async refreshPendingRollback(): Promise<void> {
    this.loadingLatestApply = true;
    try {
      const latest = await this.renameContentApi.getLatestApply();
      this.pendingRollback = latest?.available ? latest : null;
    } catch {
      this.pendingRollback = null;
    } finally {
      this.loadingLatestApply = false;
    }
  }

  private setPendingRollbackFromApply(report: RenameContentRunReport): void {
    if (!report.applyId || (report.appliedCount ?? 0) <= 0) {
      this.pendingRollback = null;
      return;
    }
    const appliedChanges = (report.changes || []).filter((c) => c.applied === true);
    this.pendingRollback = {
      success: true,
      available: true,
      applyId: report.applyId,
      planId: report.planId,
      from: report.from,
      to: report.to,
      appliedAt: report.appliedAt || new Date().toISOString(),
      scope: report.scope,
      changeCount: appliedChanges.length,
      appliedCount: report.appliedCount ?? appliedChanges.length,
      safeChangeCount: report.safeChangeCount,
      specialChangeCount: report.specialChangeCount,
      changes: appliedChanges,
      triggeredBy: report.triggeredBy,
      note: 'Last successful Apply — available for rollback for up to 1 month.',
    };
  }

  async discoveryScan(): Promise<void> {
    const term = this.from.trim();
    if (!term) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing search term',
        detail: 'Enter a "From" value to scan for.',
      });
      return;
    }

    this.loading = true;
    this.loadingLabel = 'Discovery scan in progress… this can take a while on a full scan.';
    try {
      const report = await this.renameContentApi.scan(term, this.currentScope());
      this.lastScan = report;
      this.resultMode = 'scan';
      this.runChanges = [];
      this.scanRows = this.flattenScan(report);
      this.selectedKeys = {};

      if (report.success === false) {
        this.messageService.add({
          severity: 'error',
          summary: 'Scan failed',
          detail: report.errorMessage || 'Unknown error',
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Discovery scan complete',
        detail: this.summaryText,
      });
    } catch (error: any) {
      this.handleHttpError(error, 'Scan failed');
    } finally {
      this.loading = false;
    }
  }

  downloadJson(): void {
    let payload: unknown;
    let filename: string;
    if (this.resultMode === 'scan' && this.lastScan) {
      payload = this.lastScan;
      filename = `rename-discovery-scan-${Date.now()}.json`;
    } else if (this.resultMode === 'apply' && this.lastApply) {
      const all = this.lastApply.changes || [];
      payload = {
        ...this.lastApply,
        reportType: 'apply',
        appliedChanges: all.filter((c) => c.applied === true),
        discardedChanges: all.filter((c) => c.applied === false),
      };
      filename = `rename-apply-report-${Date.now()}.json`;
    } else if (this.resultMode === 'rollback' && this.lastApply) {
      payload = {
        ...this.lastApply,
        reportType: 'rollback',
      };
      filename = `rename-rollback-report-${Date.now()}.json`;
    } else if (this.lastDryRun) {
      payload = {
        ...this.lastDryRun,
        reportType: 'dry-run',
        selectedSafeCount: this.selectedSafeCount,
        discardedKeys: this.discardedKeys,
      };
      filename = `rename-dry-run-${Date.now()}.json`;
    } else {
      return;
    }

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadPdf(): void {
    try {
      const { safeRows, specialRows, title, subtitle, note, mode, environment, durationMs } =
        this.buildPdfRows();

      downloadRenameContentPdf({
        title,
        subtitle,
        from: this.from.trim(),
        to: this.resultMode === 'scan' ? undefined : this.to.trim(),
        mode,
        environment,
        durationMs,
        note,
        safeRows,
        specialRows,
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'PDF report failed',
        detail: error?.message || 'Could not open the PDF report window.',
      });
    }
  }

  private buildPdfRows(): {
    safeRows: PdfReportRow[];
    specialRows: PdfReportRow[];
    title: string;
    subtitle?: string;
    note?: string;
    mode?: string;
    environment?: string;
    durationMs?: number;
  } {
    if (this.resultMode === 'scan' && this.lastScan) {
      const safeRows = this.scanRows
        .filter((r) => !r.isSpecialCase)
        .map((r) => this.toPdfRow(r));
      const specialRows = this.scanRows
        .filter((r) => r.isSpecialCase)
        .map((r) => this.toPdfRow(r));
      return {
        safeRows,
        specialRows,
        title: 'Content Rename — Discovery Scan Report',
        subtitle: `Search term: ${this.lastScan.searchTerm || this.from}`,
        note: this.lastScan.note,
        mode: 'scan',
        environment: this.lastScan.environment,
        durationMs: this.lastScan.durationMs,
      };
    }

    const report =
      this.resultMode === 'apply' || this.resultMode === 'rollback'
        ? this.lastApply
        : this.lastDryRun;
    const changes = report?.changes || [];

    if (this.resultMode === 'apply' && report) {
      const applied = changes.filter((c) => c.applied === true);
      return {
        safeRows: applied.filter((c) => !c.isSpecialCase).map((c) => this.toPdfRowFromChange(c)),
        specialRows: applied.filter((c) => !!c.isSpecialCase).map((c) => this.toPdfRowFromChange(c)),
        title: 'Content Rename — Apply Report',
        subtitle:
          `Applied ${report.appliedCount ?? applied.length}` +
          ` · discarded ${report.discardedCount ?? 0}` +
          (report.applyId ? ` · applyId ${report.applyId}` : ''),
        note: report.note,
        mode: 'apply',
        environment: report.environment,
        durationMs: report.durationMs,
      };
    }

    if (this.resultMode === 'rollback' && report) {
      return {
        safeRows: changes.filter((c) => !c.isSpecialCase).map((c) => this.toPdfRowFromChange(c)),
        specialRows: changes.filter((c) => !!c.isSpecialCase).map((c) => this.toPdfRowFromChange(c)),
        title: 'Content Rename — Rollback Report',
        subtitle: `Restored ${report.appliedCount ?? changes.length} value(s)`,
        note: report.note,
        mode: 'rollback',
        environment: report.environment,
        durationMs: report.durationMs,
      };
    }

    const safe =
      this.resultMode === 'dry-run'
        ? changes.filter((c) => !c.isSpecialCase && c.key && this.selectedKeys[c.key])
        : changes.filter((c) => !c.isSpecialCase);
    return {
      safeRows: safe.map((c) => this.toPdfRowFromChange(c)),
      specialRows: changes.filter((c) => c.isSpecialCase).map((c) => this.toPdfRowFromChange(c)),
      title: `Content Rename — ${report?.mode || 'report'}`,
      note: report?.note,
      mode: report?.mode,
      environment: report?.environment,
      durationMs: report?.durationMs,
    };
  }

  private toPdfRow(row: ScanRow): PdfReportRow {
    return {
      source: row.source,
      collectionOrTable: row.collectionOrTable,
      id: row.id,
      fieldOrColumn: row.fieldOrColumn,
      value: row.value,
      isSpecialCase: row.isSpecialCase,
      specialReasons: row.specialReasons,
    };
  }

  private toPdfRowFromChange(change: RenameContentChange): PdfReportRow {
    return {
      source: change.source,
      collectionOrTable: change.collectionOrTable,
      id: change.id,
      fieldOrColumn: change.fieldOrColumn,
      value: change.oldValue,
      newValue: change.newValue,
      isSpecialCase: change.isSpecialCase,
      specialReasons: change.specialReasons,
    };
  }

  private flattenScan(report: RenameContentScanReport): ScanRow[] {
    const rows: ScanRow[] = [];

    for (const doc of report.mongo?.actionable || []) {
      for (const match of doc.matches || []) {
        rows.push({
          source: 'mongo',
          collectionOrTable: doc.collection,
          id: doc.id ?? doc._id,
          fieldOrColumn: match.path || '',
          value: match.value,
          isSpecialCase: match.isSpecialCase,
          specialReasons: match.specialReasons,
        });
      }
    }

    for (const row of report.mysql?.actionable || []) {
      for (const col of row.matchedColumns || []) {
        rows.push({
          source: 'mysql',
          collectionOrTable: row.table,
          id: row.recordId ?? row.field,
          fieldOrColumn: col.column || '',
          value: col.value,
          isSpecialCase: col.isSpecialCase,
          specialReasons: col.specialReasons,
        });
      }
    }

    return [
      ...rows.filter((r) => !r.isSpecialCase),
      ...rows.filter((r) => r.isSpecialCase),
    ];
  }

  private validateInputs(): boolean {
    if (!this.isAdmin) {
      this.messageService.add({
        severity: 'error',
        summary: 'Forbidden',
        detail: 'ROLE_ADMIN is required.',
      });
      return false;
    }
    if (!this.from.trim() || !this.to.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing values',
        detail: 'Both From and To are required.',
      });
      return false;
    }
    if (this.from.trim() === this.to.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid values',
        detail: 'From and To must be different.',
      });
      return false;
    }
    return true;
  }

  private handleHttpError(error: any, summary: string): void {
    const detail =
      error?.error?.errorMessage ||
      error?.error?.error ||
      error?.error?.message ||
      error?.message ||
      'Request failed';
    const status = error?.status;
    this.messageService.add({
      severity: 'error',
      summary: status === 403 ? 'Forbidden' : summary,
      detail: status === 403 ? 'ROLE_ADMIN is required.' : detail,
    });
  }
}
